import React from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { getActivities, getHealthRange } from '../api/trainer';
import { Activity } from '../api/types';
import ChartCard from '../components/ChartCard';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { formatNumber, formatPace, isoDaysAgo, isoLocal, shortDate } from '../utils/format';
import { isRun, weeklyVolume } from '../utils/training';

const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 8, right: 8, bottom: 0, left: -12 };
const TREND_DAYS = 90;

/** Every page of activities since `from` (capped). */
async function activitiesSince(from: string): Promise<Activity[]> {
  const all: Activity[] = [];
  for (let page = 0; page < 5; page++) {
    const result = await getActivities({ from, page, size: 100 });
    all.push(...result.items);
    if (page + 1 >= result.totalPages) break;
  }
  return all;
}

const FitnessPage: React.FC = () => {
  const data = useAsync(async () => {
    const from = isoDaysAgo(TREND_DAYS);
    const to = isoDaysAgo(0);
    const [activities, daily, hrv, battery] = await Promise.all([
      activitiesSince(from),
      getHealthRange('daily', { from, to }),
      getHealthRange('hrv', { from, to }),
      getHealthRange('body-battery', { from, to }),
    ]);
    const runs = activities
      .filter(isRun)
      .sort((x, y) => x.startTime.localeCompare(y.startTime));
    return {
      runs,
      totalKm: activities.reduce((sum, a) => sum + (a.distanceM ?? 0), 0) / 1000,
      volume: weeklyVolume(activities, 12),
      paceRows: runs
        .filter(r => r.avgPaceSPerKm != null)
        .map(r => ({ date: shortDate(isoLocal(new Date(r.startTime))), pace: r.avgPaceSPerKm })),
      hrRows: runs
        .filter(r => r.avgHr != null)
        .map(r => ({ date: shortDate(isoLocal(new Date(r.startTime))), hr: r.avgHr })),
      restingRows: daily.map(d => ({ date: shortDate(d.date), resting: d.restingHr })),
      hrvRows: hrv.map(d => ({ date: shortDate(d.date), weekly: d.weeklyAvg, lastNight: d.lastNightAvg })),
      batteryRows: battery.map(d => ({ date: shortDate(d.date), high: d.highest, low: d.lowest })),
    };
  }, []);

  const d = data.data;
  const averagePace = d && d.paceRows.length
    ? d.paceRows.reduce((sum, r) => sum + (r.pace ?? 0), 0) / d.paceRows.length
    : null;

  return (
    <>
      <PageHeader title="Fitness">
        <p>Pace and heart rate trends from your runs, weekly volume, and recovery signals from your health data.</p>
      </PageHeader>

      <Alert variant="info" className="py-2">
        VO2max not available from the Garmin personal API yet. This page shows only data the API returns.
      </Alert>

      <StatusMessage loading={data.loading} error={data.error} onRetry={data.reload} />

      {d && (
        <>
          <Row className="g-3 mb-3">
            <Col xs={6} md={4}>
              <section className="surface">
                <div className="stat-label">Runs, last {TREND_DAYS} days</div>
                <div className="stat-value tabular">{d.runs.length}</div>
                <div className="stat-hint">{formatNumber(d.totalKm, 1)} km in all activities</div>
              </section>
            </Col>
            <Col xs={6} md={4}>
              <section className="surface">
                <div className="stat-label">Average run pace</div>
                <div className="stat-value tabular">{averagePace == null ? '—' : formatPace(averagePace)}</div>
                <div className="stat-hint">mean of run averages</div>
              </section>
            </Col>
            <Col xs={12} md={4}>
              <section className="surface">
                <div className="stat-label">Resting HR (latest)</div>
                <div className="stat-value tabular">
                  {formatNumber(d.restingRows[d.restingRows.length - 1]?.resting)} bpm
                </div>
                <div className="stat-hint">HRV 7-day avg {formatNumber(d.hrvRows[d.hrvRows.length - 1]?.weekly)} ms</div>
              </section>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <ChartCard title="Run pace (s/km, lower is faster)" empty={d.paceRows.length === 0}>
                <LineChart data={d.paceRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...AXIS} reversed domain={['dataMin - 10', 'dataMax + 10']} />
                  <Tooltip />
                  <Line type="monotone" dataKey="pace" name="Avg pace" stroke="var(--chart-2)" strokeWidth={2} dot />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Run heart rate (avg bpm)" empty={d.hrRows.length === 0}>
                <LineChart data={d.hrRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...AXIS} domain={['dataMin - 5', 'dataMax + 5']} />
                  <Tooltip />
                  <Line type="monotone" dataKey="hr" name="Avg HR" stroke="var(--chart-4)" strokeWidth={2} dot />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Weekly distance (km, last 12 weeks)" empty={d.volume.every(v => v.km === 0)}>
                <BarChart data={d.volume} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="week" {...AXIS} />
                  <YAxis {...AXIS} />
                  <Tooltip />
                  <Bar dataKey="km" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Resting heart rate (bpm)" empty={d.restingRows.length === 0}>
                <LineChart data={d.restingRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...AXIS} domain={['dataMin - 3', 'dataMax + 3']} />
                  <Tooltip />
                  <Line type="monotone" dataKey="resting" stroke="var(--chart-4)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="HRV (ms)" empty={d.hrvRows.length === 0}>
                <LineChart data={d.hrvRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...AXIS} domain={['dataMin - 5', 'dataMax + 5']} />
                  <Tooltip />
                  <Line type="monotone" dataKey="weekly" name="7-day avg" stroke="var(--chart-3)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Body battery (high and low)" empty={d.batteryRows.length === 0}>
                <LineChart data={d.batteryRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...AXIS} domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="high" stroke="var(--chart-1)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="low" stroke="var(--chart-2)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default FitnessPage;
